// Step 4: Mandate Signature Component (Firma del Mandato con Representación)
// Paso del hiring flow para firmar el mandato con firma manuscrita digital

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DocumentTextIcon,
  CheckCircleIcon as CheckCircleSolid,
} from '@heroicons/react/24/solid';
import {
  ExclamationCircleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { SignaturePad } from './SignaturePad';
import type { HiringDetails } from '@/types/hiring';

interface MandateSignatureProps {
  hiringCode: string;
  details: HiringDetails;
  isAlreadySigned?: boolean;
  onComplete: (signatureBase64?: string) => void;
  onBack: () => void;
}

export function MandateSignature({
  hiringCode,
  details,
  isAlreadySigned = false,
  onComplete,
  onBack,
}: MandateSignatureProps) {
  const { t } = useTranslation('mandate');
  const [signatureBase64, setSignatureBase64] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignatureChange = useCallback((base64: string | null) => {
    setSignatureBase64(base64);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación: canvas vacío
    if (!signatureBase64) {
      setError(t('errorNoSignature'));
      return;
    }

    // Validación: aceptación de términos
    if (!accepted) {
      setError(t('errorNoAccept'));
      return;
    }

    // Validación: firma con suficiente contenido (base64 muy corto = pocos trazos)
    // Una firma válida en PNG base64 debería tener al menos ~2KB de datos
    const signatureDataLength = signatureBase64.length;
    if (signatureDataLength < 2000) {
      setError(t('errorTooShort'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Guardar firma en localStorage para uso posterior en PDF definitivo
      localStorage.setItem(`mandate_signature_${hiringCode}`, signatureBase64);
      localStorage.setItem(`mandate_accepted_${hiringCode}`, 'true');

      // Generar y subir el mandato PDF
      let mandateBlob: Blob;
      try {
        const { generateMandatePDF } = await import('@/utils/mandatePdfGenerator');
        mandateBlob = generateMandatePDF(details, {
          signatureBase64,
          signatureDate: new Date().toISOString(),
          isDraft: false,
        });
      } catch (pdfError) {
        console.error('Error generando PDF del mandato:', pdfError);
        setError(t('errorProcessing'));
        setIsSubmitting(false);
        return;
      }

      // Subir al backend
      try {
        const { mandateService } = await import('@/services/mandateService');
        const formData = new FormData();
        formData.append('mandate', mandateBlob, `mandato_${hiringCode}.pdf`);
        formData.append('hiring_code', hiringCode);
        formData.append('signature_base64', signatureBase64);
        formData.append('accepted_at', new Date().toISOString());
        formData.append('client_name', details.client_name || '');
        formData.append('client_email', details.client_email || '');

        await mandateService.uploadMandatePDF(hiringCode, formData);
        console.log('✅ Mandato firmado y subido exitosamente');
      } catch (uploadError: unknown) {
        // Distinguir entre error de red y rechazo del backend
        const isNetworkError =
          uploadError instanceof TypeError ||
          (uploadError instanceof Error && /network|fetch|abort|timeout/i.test(uploadError.message));

        if (isNetworkError) {
          console.warn('⚠️ Error de red subiendo mandato (continuando):', uploadError);
          // No bloquear el flujo si es error de red - se reintentará después
        } else {
          console.warn('⚠️ Error subiendo mandato al backend (continuando):', uploadError);
          // No bloquear el flujo si falla la subida - se reintentará después
        }
      }

      onComplete(signatureBase64);
    } catch (err: unknown) {
      console.error('Error firmando mandato:', err);

      // Mensajes de error específicos según el tipo de fallo
      const isNetworkError =
        err instanceof TypeError ||
        (err instanceof Error && /network|fetch|abort|timeout/i.test(err.message));

      if (isNetworkError) {
        setError(t('errorNetwork'));
      } else {
        setError(t('errorProcessing'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePreviewMandate = async () => {
    try {
      const { generateMandatePDF } = await import('@/utils/mandatePdfGenerator');
      const blob = generateMandatePDF(details, {
        signatureBase64: signatureBase64 || undefined,
        signatureDate: new Date().toISOString(),
        isDraft: true,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Liberar después de un breve delay
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('Error generando preview del mandato:', err);
    }
  };

  const handleDownloadMandate = async () => {
    try {
      const { generateMandatePDF } = await import('@/utils/mandatePdfGenerator');
      const blob = generateMandatePDF(details, {
        signatureBase64: signatureBase64 || undefined,
        signatureDate: new Date().toISOString(),
        isDraft: true,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mandato-borrador-${hiringCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error descargando mandato:', err);
    }
  };

  const handleDownloadSignedMandate = () => {
    if (details.mandate_pdf_url) {
      window.open(details.mandate_pdf_url, '_blank', 'noopener,noreferrer');
    } else {
      import('@/services/mandateService').then(({ mandateService }) => {
        mandateService.downloadMandateFile(hiringCode).catch(() => {
          toast.error('No se pudo descargar el mandato.');
        });
      });
    }
  };

  // Si ya está firmado, mostrar mensaje, enlace de descarga y botón para continuar
  if (isAlreadySigned) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card className="shadow-lg border-2 border-green-300 bg-green-50">
          <CardHeader className="bg-gradient-to-r from-green-100 to-green-50">
            <CardTitle className="text-2xl text-green-900 flex items-center gap-2">
              <CheckCircleSolid className="text-green-600" width={28} height={28} />
              {t('alreadySigned')}
            </CardTitle>
          </CardHeader>

          <CardContent className="pt-6 space-y-6">
            <div className="bg-white border border-green-200 rounded-lg p-6">
              <p className="text-lg text-green-900 mb-4">
                {t('alreadySignedDesc')}
              </p>
              <p className="text-sm text-green-700">
                {t('alreadySignedNote')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleDownloadSignedMandate}
                variant="outline"
                className="border-green-600 text-green-700 hover:bg-green-50 gap-2 min-h-[44px]"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                {t('downloadMandate')}
              </Button>
            </div>

            <div className="flex gap-4">
              <Button onClick={onBack} variant="outline" className="flex-1">
                {t('common:buttons.back')}
              </Button>
              <Button
                onClick={() => onComplete()}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {t('common:buttons.continue')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6">
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10">
          <CardTitle className="text-xl md:text-2xl text-emphasis-900 flex items-center gap-2">
            <DocumentTextIcon className="text-primary" width={28} height={28} />
            {t('title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          {/* Resumen del mandato */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-blue-900 mb-3">
              {t('whatIsMandate')}
            </h3>
            <p className="text-sm text-blue-800 mb-3">
              {t('mandateDescription')}
            </p>
            <p className="text-sm text-blue-700">
              {t('mandateLegalBasis')}
            </p>
          </div>

          {/* Datos del mandante */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 md:p-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              {t('mandatorData')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">{t('name')}</span>
                <span className="ml-2 font-medium text-gray-900">{details.client_name || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('emailLabel')}</span>
                <span className="ml-2 font-medium text-gray-900">{details.client_email || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('passport')}</span>
                <span className="ml-2 font-medium text-gray-900">
                  {details.client_passport || details.user_passport || '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">{t('nie')}</span>
                <span className="ml-2 font-medium text-gray-900">
                  {details.client_nie || details.user_nie || '-'}
                </span>
              </div>
            </div>
          </div>

          {/* Acciones de preview/descarga */}
          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePreviewMandate}
              className="text-blue-600 hover:text-blue-700"
            >
              <EyeIcon className="w-4 h-4 mr-1" />
              {t('viewFullMandate')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadMandate}
              className="text-gray-600"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
              {t('downloadDraft')}
            </Button>
          </div>

          {/* Formulario de firma */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Canvas de firma */}
            <div className="space-y-2">
              <label className="text-base font-semibold text-gray-900 block">
                {t('signatureLabel')}
              </label>
              <p className="text-sm text-gray-500 mb-3">
                {t('signatureHint')}
              </p>

              <SignaturePad
                onSignatureChange={handleSignatureChange}
                height={220}
                hintText={t('signHere')}
                disabled={isSubmitting}
              />
            </div>

            {/* Checkbox de aceptación */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <Checkbox
                id="mandate-accept"
                checked={accepted}
                onCheckedChange={(checked) => {
                  setAccepted(checked === true);
                  setError(null);
                }}
                disabled={isSubmitting}
                className="mt-0.5"
              />
              <label
                htmlFor="mandate-accept"
                className="text-sm text-amber-900 cursor-pointer leading-relaxed"
              >
                {t('acceptTerms')}
              </label>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-start gap-2">
                <ExclamationCircleIcon width={20} height={20} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">{t('error')}</p>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Indicador de firma válida */}
            {signatureBase64 && accepted && (
              <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg flex items-start gap-2">
                <CheckCircleSolid width={20} height={20} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">{t('readyToSign')}</p>
                  <p className="text-sm">
                    {t('readyToSignDesc')}
                  </p>
                </div>
              </div>
            )}

            {/* Nota legal */}
            <div className="bg-gray-50 border-l-4 border-primary p-4 rounded">
              <p className="text-xs text-gray-600">
                {t('legalNote')}
              </p>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                onClick={onBack}
                variant="outline"
                className="flex-1"
              >
                {t('common:buttons.back')}
              </Button>
              <Button
                type="submit"
                disabled={!signatureBase64 || !accepted || isSubmitting}
                className="flex-1 bg-primary hover:bg-primary-700 text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('signing')}
                  </span>
                ) : signatureBase64 && accepted ? (
                  <>
                    <CheckCircleSolid className="mr-2" width={18} height={18} />
                    {t('signMandate')}
                  </>
                ) : (
                  t('signMandate')
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
